/**
 * Firebase Logger Adapter
 *
 * Adapts the centralized Logger to work with the existing Firebase logging infrastructure.
 */

import { logEvent } from './firebase'
import { LogEntry, LogLevel } from './Logger'

/**
 * Convert LogLevel enum to Firebase log type string
 */
function logLevelToFirebaseType(
    level: LogLevel
): 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' {
    switch (level) {
        case LogLevel.DEBUG:
            return 'DEBUG'
        case LogLevel.INFO:
            return 'INFO'
        case LogLevel.WARN:
            return 'WARN'
        case LogLevel.ERROR:
            return 'ERROR'
        default:
            return 'INFO'
    }
}

/**
 * Serialize error object for Firebase
 */
function serializeError(error?: Error): Record<string, unknown> | undefined {
    if (!error) {
        return undefined
    }

    return {
        name: error.name,
        message: error.message,
        stack: error.stack,
    }
}

/**
 * Create a remote logger function that sends logs to Firebase
 */
export function createFirebaseRemoteLogger(): (
    entry: LogEntry
) => Promise<void> {
    return async (entry: LogEntry): Promise<void> => {
        const type = logLevelToFirebaseType(entry.level)
        const event = entry.context?.action || 'app_log'
        const message = entry.message

        // Build metadata object
        const meta: Record<string, unknown> = {
            timestamp: entry.timestamp,
            ...(entry.context?.metadata || {}),
        }

        // Add error information if present
        if (entry.error) {
            meta.error = serializeError(entry.error)
        }

        // Add route if present
        if (entry.context?.route) {
            meta.route = entry.context.route
        }

        // Log to Firebase
        await logEvent(type, event, message, meta)
    }
}
